import { useEffect, useRef, useState } from "react";
import { useCollision } from "../hooks/use-collision";
import { usePosition } from "../hooks/use-position";
import { useWorld } from "../hooks/use-world";
import { useNavigate } from "../hooks/use-navigate";
import { usePathfinding } from "../hooks/use-pathfinding";
import { moveToTarget, snapToGap } from "../lib/behaviours";
import {
  GRID_SIZE,
  PLAYER_STRIDE,
  SNAP_THRESHOLD,
  ALIGN_THRESHOLD,
} from "../config/constants";

const dirIncludes = (dir, ...keys) =>
  keys.some((k) => (Array.isArray(dir) ? dir.includes(k) : dir === k));

export const Player = ({
  x = 40,
  y = 40,
  width = GRID_SIZE,
  height = GRID_SIZE,
  config = {},
  ...rest
}) => {
  const cfg = { stride: PLAYER_STRIDE, solid: true, center: true, ...config };
  const [position, updatePosition] = usePosition(x, y, width, height, cfg);

  const [, updateNav, navRef] = useNavigate(position, cfg);

  const posRef = useRef(position);
  useEffect(() => { posRef.current = position; }, [position]);

  const lastDirectionRef = useRef(["right"]);

  const [path, setPath] = useState([]);
  const pathRef = useRef([]);
  const pathIdxRef = useRef(0);
  const stuckRef = useRef(0);
  const activeLadderKeyRef = useRef(null);
  const ladderCooldownRef = useRef(0);
  const onExitTriggeredRef = useRef(false);
  const spawnCheckRef = useRef({ x, y });
  const autoDefenceCooldownRef = useRef(0);
  useEffect(() => { pathRef.current = path; }, [path]);

  const {
    addSprite,
    removeSprite,
    addToInventory,
    addToWallet,
    canPickUp,
    setStatusMsg,
    frame,
    spritesRef,
    target,
    setTarget,
    takeDamage,
    dealDamage,
    attackFlashRef,
    inventory,
    equippedWeaponId,
    pickupDroppedItem,
    removeFromInventory,
    autoDefence,
    pendingSpawn,
    setPendingSpawn,
    setPendingLadderChoice,
    setCurrentLevel,
    currentLevel,
    pendingLadderChoiceRef,
    onExit,
    remotePlayers,
    collectTreasure,
    quests,
    questProgress,
    completeQuestStep,
    dungeonSlug,
    paused,
  } = useWorld();
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const remotePlayersRef = useRef(remotePlayers);
  useEffect(() => { remotePlayersRef.current = remotePlayers; }, [remotePlayers]);

  const targetRef = useRef(target);
  useEffect(() => { targetRef.current = target; }, [target]);

  const { isColliding, isInteracting, doObjectsCollide } = useCollision();
  const { findPath } = usePathfinding();

  // Spacebar attack — always-fresh closure via ref so the listener sees current inventory
  const attackRef = useRef(null);
  attackRef.current = () => {
    const weapon = equippedWeaponId ? inventory?.find((item) => item.id === equippedWeaponId) : null;
    const attackDamage = weapon?.value || 5;
    const pos = posRef.current;
    const dir = lastDirectionRef.current;
    const playerCol = Math.floor(pos.x / GRID_SIZE);
    const playerRow = Math.floor(pos.y / GRID_SIZE);
    const atkCol = playerCol + (dir.includes("right") ? 1 : dir.includes("left") ? -1 : 0);
    const atkRow = playerRow + (dir.includes("down") ? 1 : dir.includes("up") ? -1 : 0);
    let hit = false;
    Object.values(spritesRef.current).forEach((s) => {
      if (s.position?.config?.entityType !== "monster") return;
      if (s.position?.config?.level !== currentLevel) return;
      if (Math.floor(s.position.x / GRID_SIZE) === atkCol && Math.floor(s.position.y / GRID_SIZE) === atkRow) {
        dealDamage(s.position.id, attackDamage);
        hit = true;
      }
    });
    attackFlashRef.current = {
      gx: atkCol * GRID_SIZE + GRID_SIZE / 2,
      gy: atkRow * GRID_SIZE + GRID_SIZE / 2,
      framesLeft: 10,
      hit,
    };
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.code !== "Space") return;
      if (pausedRef.current) return;
      e.preventDefault();
      attackRef.current();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Compute new path when click-to-move target is set
  useEffect(() => {
    if (!target) return;
    const pos = posRef.current;
    const newPath = findPath(pos, target, width, height);
    if (newPath.length === 0) {
      setPath([]);
      pathRef.current = [];
      return;
    }
    const gridX = Math.floor(pos.x / GRID_SIZE) * GRID_SIZE;
    const gridY = Math.floor(pos.y / GRID_SIZE) * GRID_SIZE;
    if (newPath[0].x !== gridX || newPath[0].y !== gridY) {
      const snap = newPath[0];
      posRef.current = { ...pos, x: snap.x, y: snap.y };
      navRef.current = { ...pos, x: snap.x, y: snap.y };
      updatePosition(snap.x, snap.y, {});
      updateNav({ ...pos, x: snap.x, y: snap.y });
    }
    pathRef.current = newPath;
    setPath(newPath);
    pathIdxRef.current = newPath.length > 1 ? 1 : 0;
  }, [target]);

  // BFS outward from (tx, ty) to find the nearest free, non-ladder grid cell
  const findFreeSpawn = (tx, ty) => {
    const probe = { id: "__spawn__", width: GRID_SIZE, height: GRID_SIZE };
    const startCol = Math.round(tx / GRID_SIZE);
    const startRow = Math.round(ty / GRID_SIZE);
    const visited = new Set([`${startCol},${startRow}`]);
    const queue = [[startCol, startRow]];
    const remotes = Object.values(remotePlayersRef.current);
    const isOccupiedByRemote = (col, row) =>
      remotes.some((rp) => rp.x != null && rp.y != null && Math.floor(rp.x / GRID_SIZE) === col && Math.floor(rp.y / GRID_SIZE) === row);
    const isLadderAt = (col, row) => {
      const box = { x: col * GRID_SIZE, y: row * GRID_SIZE, width: GRID_SIZE, height: GRID_SIZE };
      return Object.values(spritesRef.current).some(
        (s) => s.position?.config?.entityType === "ladder" && doObjectsCollide(box, s.position)
      );
    };
    while (queue.length) {
      const [col, row] = queue.shift();
      if (!isColliding({ ...probe, x: col * GRID_SIZE, y: row * GRID_SIZE }) && !isOccupiedByRemote(col, row) && !isLadderAt(col, row)) {
        return { x: col * GRID_SIZE, y: row * GRID_SIZE };
      }
      for (const [dc, dr] of [[0,1],[0,-1],[1,0],[-1,0],[1,1],[-1,-1],[1,-1],[-1,1]]) {
        const key = `${col + dc},${row + dr}`;
        if (!visited.has(key)) { visited.add(key); queue.push([col + dc, row + dr]); }
      }
    }
    return { x: tx, y: ty };
  };

  // Teleport to pendingSpawn when a ladder is used; defer BFS safety check to next frame
  useEffect(() => {
    if (!pendingSpawn) return;
    const { x, y } = pendingSpawn;
    const pos = posRef.current;
    posRef.current = { ...pos, x, y };
    navRef.current = { ...pos, x, y };
    updatePosition(x, y, {});
    updateNav({ ...pos, x, y });
    setPath([]);
    pathRef.current = [];
    pathIdxRef.current = 0;
    setTarget(null);
    setPendingSpawn(null);
    spawnCheckRef.current = { x, y };
    ladderCooldownRef.current = 30;
    const probe = { id: "__spawn__", x, y, width: GRID_SIZE, height: GRID_SIZE };
    const allLadderSprites = Object.values(spritesRef.current).filter(s => s.position?.config?.entityType === "ladder");
    const returnLadderKey = allLadderSprites
      .filter(s => doObjectsCollide(probe, s.position))
      .map(s => s.position.id)
      .sort()
      .join(",");
    if (returnLadderKey) activeLadderKeyRef.current = returnLadderKey;
  }, [pendingSpawn]);

  // ── Frame subsystem helpers ─────────────────────────────────────────────────
  // Each function reads from refs so it always sees the latest values without
  // being listed in the frame effect's dependency array.

  const runSpawnSafety = () => {
    const checkPos = spawnCheckRef.current ?? posRef.current;
    const probe = { id: "player", x: checkPos.x, y: checkPos.y, width: GRID_SIZE, height: GRID_SIZE };
    const solidCollision = isColliding(probe);
    const ladderAtSpawn = spawnCheckRef.current != null && !activeLadderKeyRef.current &&
      Object.values(spritesRef.current).some(
        (s) => s.position?.config?.entityType === "ladder" && doObjectsCollide(probe, s.position)
      );
    if (solidCollision?.position?.config?.solid || ladderAtSpawn) {
      const { x, y } = findFreeSpawn(checkPos.x, checkPos.y);
      spawnCheckRef.current = null;
      posRef.current = { ...posRef.current, x, y };
      navRef.current = { ...navRef.current, x, y };
      updatePosition(x, y, {});
      updateNav({ ...navRef.current, x, y });
      return true;
    }
    spawnCheckRef.current = null;
    return false;
  };

  const runAutoDefence = (pos) => {
    if (!autoDefence) return;
    if (autoDefenceCooldownRef.current > 0) { autoDefenceCooldownRef.current--; return; }
    const playerCol = Math.floor(pos.x / GRID_SIZE);
    const playerRow = Math.floor(pos.y / GRID_SIZE);
    let nearestMonster = null;
    let nearestDist = Infinity;
    Object.values(spritesRef.current).forEach((s) => {
      if (s.position?.config?.entityType !== "monster") return;
      if (s.position?.config?.level !== currentLevel) return;
      const dc = Math.floor(s.position.x / GRID_SIZE) - playerCol;
      const dr = Math.floor(s.position.y / GRID_SIZE) - playerRow;
      if (Math.abs(dc) <= 1 && Math.abs(dr) <= 1 && !(dc === 0 && dr === 0)) {
        const dist = Math.abs(dc) + Math.abs(dr);
        if (dist < nearestDist) { nearestDist = dist; nearestMonster = { s, dc, dr }; }
      }
    });
    if (nearestMonster) {
      const { s, dc, dr } = nearestMonster;
      const weapon = equippedWeaponId ? inventory?.find((item) => item.id === equippedWeaponId) : null;
      dealDamage(s.position.id, weapon?.value || 5);
      const dir = [dc > 0 ? "right" : dc < 0 ? "left" : "", dr > 0 ? "down" : dr < 0 ? "up" : ""].filter(Boolean);
      lastDirectionRef.current = dir.length ? dir : lastDirectionRef.current;
      attackFlashRef.current = {
        gx: (Math.floor(pos.x / GRID_SIZE) + dc) * GRID_SIZE + GRID_SIZE / 2,
        gy: (Math.floor(pos.y / GRID_SIZE) + dr) * GRID_SIZE + GRID_SIZE / 2,
        framesLeft: 10,
        hit: true,
      };
      autoDefenceCooldownRef.current = 30;
    }
  };

  const runLadderDetection = (pos) => {
    if (ladderCooldownRef.current > 0) { ladderCooldownRef.current--; return; }
    const ladders = Object.values(spritesRef.current).filter(
      (s) => s.position?.config?.entityType === "ladder" && doObjectsCollide(pos, s.position)
    );
    const ladderKey = ladders.map((s) => s.position.id).sort().join(",");
    if (ladders.length === 0) {
      activeLadderKeyRef.current = null;
    } else if (ladderKey !== activeLadderKeyRef.current && !pendingLadderChoiceRef.current) {
      activeLadderKeyRef.current = ladderKey;
      if (ladders.length === 1) {
        const c = ladders[0].position.config;
        setPendingSpawn({ x: c.targetX ?? 40, y: c.targetY ?? 40 });
        setCurrentLevel(c.targetLevel ?? 0);
      } else {
        setPendingLadderChoice(ladders.map((s) => s.position.config));
      }
    }
  };

  const pickUpTreasuresAt = (checkPos) => {
    Object.values(spritesRef.current).forEach((s) => {
      const et = s.position?.config?.entityType;
      if (!(et === "item" || et === "treasure")) return;
      if (s.position?.config?.level !== currentLevel) return;
      if (!doObjectsCollide(checkPos, s.position)) return;
      const treasureType = s.position.config?.treasureType || "gold";
      const treasureBehavior = s.position.config?.treasureBehavior || "normal";
      const itemValue = s.position.config?.value || 10;
      if (treasureType === "gold") {
        addToWallet(itemValue);
      } else {
        const itemName = s.position.config?.name || treasureType;
        if (!canPickUp(treasureType, itemName)) { setStatusMsg(`${treasureType} bag full`); return; }
        addToInventory({
          id: s.position.id,
          value: itemValue,
          type: treasureType,
          name: itemName,
          illumination: s.position.config?.illumination || 0,
          lifespan: s.position.config?.lifespan || 0,
        });
        setStatusMsg(`picked up ${itemName}`);
      }
      const pickedId = s.position.id;
      removeSprite({ id: pickedId });
      if (s.position.config?.dropped) {
        pickupDroppedItem?.(pickedId);
      } else {
        collectTreasure?.(pickedId, treasureBehavior);
      }
      for (const quest of quests) {
        const progress = questProgress[quest.id];
        if (!progress || progress.status !== "active") continue;
        for (const step of quest.steps) {
          if (step.type !== "collect_item") continue;
          if (step.dungeon_slug && dungeonSlug && step.dungeon_slug !== dungeonSlug) continue;
          if (step.itemEntityId === pickedId && !progress.completedSteps.includes(step.id)) {
            completeQuestStep(quest.id, step.id);
          }
        }
      }
    });
  };

  const runQuestTileChecks = (pos) => {
    if (!quests.length) return;
    const col = Math.floor(pos.x / GRID_SIZE);
    const row = Math.floor(pos.y / GRID_SIZE);
    for (const quest of quests) {
      const progress = questProgress[quest.id];
      if (!progress || progress.status !== "active") continue;
      for (const step of quest.steps) {
        if (step.type !== "place_on_tile") continue;
        if (progress.completedSteps.includes(step.id)) continue;
        if (step.dungeon_slug && dungeonSlug && step.dungeon_slug !== dungeonSlug) continue;
        if (step.levelIndex != null && step.levelIndex !== currentLevel) continue;
        if (step.tileX !== col || step.tileY !== row) continue;
        const item = inventory?.find((i) => i.type === step.itemType);
        if (!item) continue;
        removeFromInventory(item.id);
        completeQuestStep(quest.id, step.id);
      }
    }
  };

  const runExitDetection = (pos) => {
    const exits = Object.values(spritesRef.current).filter(
      (s) => s.position?.config?.entityType === "exit" && doObjectsCollide(pos, s.position)
    );
    if (exits.length === 0) {
      onExitTriggeredRef.current = false;
    } else if (!onExitTriggeredRef.current && onExit) {
      onExitTriggeredRef.current = true;
      onExit();
    }
  };

  const runMovement = (pos, nav) => {
    let nextPos = null;
    const navMoved = nav.x !== pos.x || nav.y !== pos.y;
    const currentPath = pathRef.current;
    const currentTarget = targetRef.current;

    if (navMoved) {
      nextPos = nav;
      if (nav.direction?.length) lastDirectionRef.current = nav.direction;
    } else if (currentPath.length > 0 && pathIdxRef.current < currentPath.length) {
      const waypoint = currentPath[pathIdxRef.current];
      if (Math.abs(pos.x - waypoint.x) === 0 && Math.abs(pos.y - waypoint.y) === 0) {
        pathIdxRef.current++;
        if (pathIdxRef.current >= currentPath.length) {
          setPath([]); pathRef.current = []; setTarget(null); return;
        }
      }
      const wp = currentPath[pathIdxRef.current];
      if (wp) nextPos = moveToTarget(wp.x, wp.y, pos, cfg.stride);
    }

    if (!nextPos) return;

    // Grid-line alignment nudge — helps player slide into 1-tile passages
    const dir = nextPos.direction;
    if (dirIncludes(dir, "left", "right")) {
      const nearestY = Math.round(nextPos.y / GRID_SIZE) * GRID_SIZE;
      const dy = nearestY - nextPos.y;
      if (Math.abs(dy) > 0 && Math.abs(dy) <= ALIGN_THRESHOLD) {
        const nudged = { ...nextPos, y: nextPos.y + Math.sign(dy) * Math.min(Math.abs(dy), cfg.stride) };
        if (!isColliding(nudged)) nextPos = nudged;
      }
    } else if (dirIncludes(dir, "up", "down")) {
      const nearestX = Math.round(nextPos.x / GRID_SIZE) * GRID_SIZE;
      const dx = nearestX - nextPos.x;
      if (Math.abs(dx) > 0 && Math.abs(dx) <= ALIGN_THRESHOLD) {
        const nudged = { ...nextPos, x: nextPos.x + Math.sign(dx) * Math.min(Math.abs(dx), cfg.stride) };
        if (!isColliding(nudged)) nextPos = nudged;
      }
    }

    const collision = isColliding(nextPos);
    const interactions = isInteracting(nextPos);
    // NPCs with interactionAreas own their own dialog start (npc.js approach logic) —
    // direct adjacency here would fire dialog before the NPC has walked to the trigger tile.
    const levelInteractions = interactions.filter(
      (int) =>
        int.position?.config?.level === currentLevel &&
        !int.position?.config?.interactionAreas?.length
    );
    if (levelInteractions.length > 0) {
      const nearest = levelInteractions.reduce((best, int) => {
        if (!int.position) return best;
        const dx = int.position.x - pos.x;
        const dy = int.position.y - pos.y;
        const d = dx * dx + dy * dy;
        return !best || d < best.d ? { int, d } : best;
      }, null);
      if (nearest && !nearest.int.interacting) addSprite({ ...nearest.int.position, interacting: true });
    }

    if (collision?.position?.config?.solid) {
      const snapped = snapToGap(nextPos, isColliding, SNAP_THRESHOLD);
      if (snapped) {
        pickUpTreasuresAt(collision.position);
        stuckRef.current = 0;
        posRef.current = snapped; navRef.current = snapped;
        updatePosition(snapped.x, snapped.y, { direction: snapped.direction });
        updateNav(snapped);
        return;
      }
      const ax = Math.round(pos.x / GRID_SIZE) * GRID_SIZE;
      const ay = Math.round(pos.y / GRID_SIZE) * GRID_SIZE;
      if (ax !== pos.x || ay !== pos.y) {
        const aligned = { ...pos, x: ax, y: ay };
        if (!isColliding(aligned)) {
          posRef.current = aligned; navRef.current = aligned;
          updatePosition(ax, ay, {}); updateNav(aligned);
        }
      }
    }

    if (!collision) {
      stuckRef.current = 0;
      posRef.current = nextPos; navRef.current = nextPos;
      updatePosition(nextPos.x, nextPos.y, { direction: nextPos.direction });
      updateNav(nextPos);
    } else {
      const { config: colCfg } = collision;
      if (colCfg?.entityType === "monster") takeDamage(colCfg?.damage || 5);
      pickUpTreasuresAt(collision.position ?? nextPos);
      navRef.current = pos;
      updateNav(pos);
      stuckRef.current++;
      if (currentPath.length > 0 && currentTarget) {
        if (stuckRef.current > 45) {
          setPath([]); pathRef.current = []; setTarget(null); stuckRef.current = 0;
        } else if (stuckRef.current === 1 || stuckRef.current % 15 === 0) {
          const newPath = findPath(pos, currentTarget, width, height);
          if (newPath.length > 1) {
            pathRef.current = newPath; setPath(newPath); pathIdxRef.current = 1; stuckRef.current = 0;
          } else {
            setPath([]); pathRef.current = []; setTarget(null); stuckRef.current = 0;
          }
        }
      } else {
        setTarget(null);
      }
    }
  };

  // ── Main game loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (runSpawnSafety()) return;

    const pos = posRef.current;
    const nav = navRef.current;

    runAutoDefence(pos);
    runLadderDetection(pos);
    if (pendingLadderChoiceRef.current) return;

    pickUpTreasuresAt(pos);
    runQuestTileChecks(pos);
    runExitDetection(pos);
    runMovement(pos, nav);
  }, [frame]);

  return null;
};
